import { useEffect, useMemo, useState } from "react";
import { useApi } from '../../hooks/useApi';
import { useMsal } from "@azure/msal-react";
import ChainList from "../common/ChainList";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";

export default function ChainConfigPage() {
    const [selectedChain, setSelectedChain] = useState("");
    const { get, post, del } = useApi();
    const { accounts } = useMsal();
    const user = accounts[0];
    const [amountLimit, setAmountLimit] = useState(null); // valor numérico real
    const [amountInput, setAmountInput] = useState("");  

    const [mails, setMails] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState(50);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState({ open: false, title: "", message: "" });

    const normalizeEmail = (s) => (s || "").trim().toLowerCase();
    const isDuplicate = (list, email) => list.some(x => normalizeEmail(x.email) === normalizeEmail(email));


    // --- Cargar correos de la cadena seleccionada ---
    const loadMails = async (chain) => {
        const data = await get('ChainMailsList', {
            params: { chain },                 
            showError: true,
            errorMessage: 'No se pudieron cargar los correos',
        });
        return Array.isArray(data) ? data : [];
    };

    // devuelve number | null
    const loadChainLimit = async (chain) => {
        if (!chain) return null;
        try {
            const res = await get('ChainGetLimit', { params: { chain }, showError: false });
            // res.amountLimit puede venir como number o string; normalizamos:
            const raw = res?.amountLimit;
            if (raw === undefined || raw === null) return null;
            const num = Number(raw);
            return Number.isNaN(num) ? null : num;
        } catch (err) {
            console.error("Error loadChainLimit:", err);
            return null;
        }
    };

    useEffect(() => {
        (async () => {
            if (!selectedChain) {
                setMails([]);
                setAmountLimit(null);
                setAmountInput("");
                return;
            }

            setLoading(true);
            try {
                // llamamos mails y límite en paralelo
                const [mailsResult, limitValue] = await Promise.all([
                    loadMails(selectedChain),
                    loadChainLimit(selectedChain)
                ]);

                setMails(mailsResult || []);
                setAmountLimit(limitValue);
                console.log(limitValue);

                // formatear para mostrar (si hay valor)
                setAmountInput(
                    limitValue != null
                        ? Number(limitValue).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : ""
                );
            } catch (err) {
                setMails([]);
                setAmountLimit(null);
                setAmountInput("");
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedChain]);

    const filtered = useMemo(() => {
        const q = (search || "").toLowerCase();
        return q ? mails.filter((m) => (m.email || "").toLowerCase().includes(q)) : mails;
    }, [mails, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageData = filtered.slice((page - 1) * pageSize, page * pageSize);

    // --- Agregar correo ---
    const handleAdd = async () => {
        const email = (newEmail || '').trim().toLowerCase();
        if (!selectedChain || !email) return;

        // valida
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!re.test(email)) {
            toast.error('Email inválido');
            return;
        }

        try {
            await post('ChainMailsAdd', { chain: selectedChain, email, userEmail: user.username, }, {
                showSuccess: true,
                successMessage: 'Correo agregado',
                showError: false,                 // manejamos abajo
            });

            // refresca lista y limpia filtros
            setNewEmail('');
            setSearch('');
            setPage(1);
            const fresh = await loadMails(selectedChain);
            setMails(fresh);
        } catch (err) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message || '';
            if (status === 409 || (status === 400 && /existe|duplic/i.test(msg))) {
                toast.info(`El correo ya existe para ${selectedChain}.`);
            } else {
                toast.error(msg || 'No se pudo agregar el correo.');
            }
        }
    };

    // Eliminar correo
    const handleDelete = async (email) => {
        if (!selectedChain || !email) return;
        // eslint-disable-next-line no-restricted-globals
        if (!window.confirm(`Eliminar ${email}?`)) return;

        try {
            await post(
                'ChainMailsDel',               // clave en API_URLS
                {
                    chain: selectedChain,
                    email,
                    userEmail: user.username,    // si no coincide con DmeUsers.Email, no se rompe
                },
                {
                    showSuccess: true,
                    successMessage: 'Correo eliminado',
                    showError: false,
                }
            );

            const fresh = await loadMails(selectedChain);
            setMails(fresh);
            setPage(1);
        } catch (err) {
            const msg = err?.response?.data?.message || 'No se pudo eliminar el correo.';
            toast.error(msg);
        }
    };

    const parseAmountInput = (s) => {
        if (s === null || s === undefined) return null;
        const t = String(s).trim();
        if (t === "") return null;
        // si contiene coma como decimal (y puntos como miles): eliminar puntos, cambiar coma->punto
        const both = /\d+\.\d+,\d+/.test(t); // improbable
        if (both) {
            // caso raro "1.234.567,89"
            return Number(t.replace(/\./g, "").replace(",", "."));
        }
        // si tiene "," and "." decide:
        if (t.includes(",") && t.includes(".")) {
            // si última aparición es coma -> assume comma decimal (es-MX)
            const lastComma = t.lastIndexOf(",");
            const lastDot = t.lastIndexOf(".");
            if (lastComma > lastDot) {
                return Number(t.replace(/\./g, "").replace(",", "."));
            } else {
                // último es punto -> assume point decimal (en-US)
                return Number(t.replace(/,/g, ""));
            }
        }
        // solo coma
        if (t.includes(",") && !t.includes(".")) {
            return Number(t.replace(",", "."));
        }
        // solo punto (asume punto decimal o punto thousands?)
        if (!t.includes(",") && t.includes(".")) {
            // si hay múltiples puntos probablemente sean thousands -> eliminar puntos
            if ((t.match(/\./g) || []).length > 1) {
                return Number(t.replace(/\./g, ""));
            }
            // un punto: asume decimal
            return Number(t);
        }
        // solo digits
        return Number(t);
    };


    const handleSetLimit = async () => {
        if (!selectedChain) {
            toast.error("Selecciona una cadena antes de actualizar límite.");
            return;
        }

        const parsed = parseAmountInput(amountInput);
        if (parsed === null || Number.isNaN(parsed)) {
            toast.error("Monto inválido.");
            return;
        }

        setBusy(true);
        try {
            // Llamada a tu endpoint que actualiza el límite
            await post('ChainSetLimit', {
                chain: selectedChain,
                amountLimit: parsed,
                userEmail: user.username
            }, {
                showSuccess: false,
                showError: false
            });

            // actualizar estado local y formatear input como es-MX
            setAmountLimit(parsed);
            setAmountInput(Number(parsed).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
            toast.success("Límite actualizado.");
        } catch (err) {
            console.error("ChainSetLimit error:", err);
            const msg = err?.response?.data?.message || "No fue posible actualizar el límite.";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    function InfoModal({ open, title, message, onClose }) {
        if (!open) return null;
        return (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow p-4 w-[90%] max-w-md">
                    <h3 className="font-semibold mb-2">{title}</h3>
                    <p className="text-sm text-gray-700 mb-4">{message}</p>
                    <div className="flex justify-end">
                        <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={onClose}>
                            Entendido
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form className="p-4 space-y-4">
            {/* Sección de selección de cadena */}
            <div className="bg-white rounded-xl shadow p-4">
                <h1 className="text-xl font-semibold mb-3">Configuración de cadena</h1>
                <ChainList
                    userEmail={user.username} // correo de EntraID
                    selectedChain={selectedChain}
                    setSelectedChain={setSelectedChain}
                    isLocked={false}
                />
            </div>

            <div className="bg-white rounded-xl shadow p-4 mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div>
                    <h6 className="text-m font-semibold mb-">Total máximo por folio</h6>
                    <input
                        type="text"
                        className="border rounded px-3 py-2 w-full"
                        placeholder="ej. 1.234,56"
                        value={amountInput}
                        onChange={(e) => setAmountInput(e.target.value)}
                        disabled={!selectedChain || busy}
                    />
                </div>

                <div className="sm:col-span-2 flex gap-3">
                    <button
                        type="button"
                        className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-60"
                        onClick={handleSetLimit}
                        disabled={!selectedChain || busy}
                    >
                        {busy ? "Actualizando…" : "Actualizar límite"}
                    </button>
                </div>
            </div>

            {/* Panel de correos */}
            <div className="bg-white rounded-xl shadow p-4">
                <h6 className="text-lg font-semibold mb-">Correos de reporte</h6>

                {!selectedChain ? (
                    <p className="text-sm text-gray-600">
                        Selecciona una cadena para configurar los correos de reporte.
                    </p>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-3 items-center">
                            <button
                                className="bg-green-700 text-white px-4 py-2 rounded disabled:opacity-60"
                                onClick={handleAdd}
                                type="button"
                                disabled={!newEmail || busy}
                            >
                                + Agregar correo
                            </button>

                            <label className="ml-2 text-sm text-gray-600">Registros por página:</label>
                            <select
                                className="border rounded px-2 py-2"
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                {[10, 20, 50, 100].map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </select>

                            <input
                                className="border rounded px-3 py-2 ml-auto"
                                placeholder="Buscar correo…"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                            />
                        </div>

                        <div className="mt-3">
                            <input
                                type="email"
                                className="border rounded px-3 py-2 w-full sm:w-96"
                                placeholder="nuevo@correo.com"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleAdd();
                                }}
                            />
                        </div>

                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-100 text-left text-gray-700">
                                        <th className="p-2">Email</th>
                                        <th className="p-2 w-28">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td className="p-3" colSpan={2}>
                                                Cargando…
                                            </td>
                                        </tr>
                                    ) : pageData.length === 0 ? (
                                        <tr>
                                            <td className="p-3" colSpan={2}>
                                                Sin correos.
                                            </td>
                                        </tr>
                                    ) : (
                                        pageData.map((m) => (
                                            <tr key={m.email} className="border-t">
                                                <td className="p-2">{m.email}</td>
                                                <td className="p-2">
                                                    <button
                                                        className="flex items-center gap-1 px-1.5 py-0.5 border rounded text-red-700 bg-red-100 hover:bg-red-200 text-xs"
                                                        onClick={() => handleDelete(m.email)}
                                                        type="button"
                                                        disabled={busy}
                                                    >
                                                        <Icon icon="mdi:trash-can-outline" className="text-base" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                            <button
                                className="border rounded px-3 py-1"
                                type="button"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                ←
                            </button>
                            <span className="text-sm">
                                Página {page} / {totalPages}
                            </span>
                            <button
                                className="border rounded px-3 py-1"
                                type="button"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            >
                                →
                            </button>
                        </div>
                    </>
                )}
            </div>
            <InfoModal
                open={dialog.open}
                title={dialog.title}
                message={dialog.message}
                onClose={() => setDialog({ open: false, title: "", message: "" })}
            />
        </form>
    );
}